import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import CustomFieldInput from 'app/shared/components/customFields/CustomFieldsSection/components/CustomFieldInput';
import {
  isCalculated,
  isManualReadOnly,
  isManualRequired,
} from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import PropTypes from 'prop-types';

import {
  customFieldCellStyles,
  customFieldsGroupGridStyles,
  customFieldsGroupTitleStyles,
  customFieldsGroupWrapperStyles,
} from './DrawerCustomFieldsGroup.styles';
import { resolveDefinitionWithLabel } from './DrawerCustomFieldsGroup.utils';

const DrawerCustomFieldsGroup = ({
  resolutions,
  controller,
  title,
  numberOfColumns,
  disabled = false,
  dataTestId = 'loan-editor-custom-form-custom-fields',
}) => {
  if (!resolutions.length) {
    return null;
  }

  const {
    formValues,
    errors,
    pendingChangesByDefinition = {},
    setFieldValue,
    setFieldTouched,
  } = controller;

  return (
    <Box data-testid={dataTestId} sx={customFieldsGroupWrapperStyles}>
      <Typography sx={customFieldsGroupTitleStyles} variant="medium14">
        {title}
      </Typography>
      <Box sx={customFieldsGroupGridStyles(numberOfColumns)}>
        {resolutions.map(resolution => {
          const { definition, label, item, widthInColumns, disabled: isLocked } = resolution;
          const definitionWithLabel = resolveDefinitionWithLabel({ definition, label });
          const columnSpan = Math.min(widthInColumns ?? 1, numberOfColumns);

          return (
            <Box key={item.id ?? definition.id} sx={customFieldCellStyles(columnSpan)}>
              <CustomFieldInput
                dataTestId={`${dataTestId}-${definition.id}`}
                definition={definitionWithLabel}
                disabled={
                  disabled || isLocked || isManualReadOnly(definition) || isCalculated(definition)
                }
                errorKey={errors[definition.id]}
                pendingChangesActionType={pendingChangesByDefinition[definition.id] ?? null}
                required={isManualRequired(definition)}
                value={formValues[definition.id] ?? ''}
                onBlur={setFieldTouched}
                onChange={setFieldValue}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

DrawerCustomFieldsGroup.propTypes = {
  resolutions: PropTypes.arrayOf(
    PropTypes.shape({
      item: PropTypes.object.isRequired,
      definition: PropTypes.object.isRequired,
      label: PropTypes.string,
      widthInColumns: PropTypes.number,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  controller: PropTypes.shape({
    formValues: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    pendingChangesByDefinition: PropTypes.object,
  }).isRequired,
  title: PropTypes.string.isRequired,
  numberOfColumns: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
};

DrawerCustomFieldsGroup.displayName = 'DrawerCustomFieldsGroup';

export default memo(DrawerCustomFieldsGroup);
